import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import I18n from 'i18n-react';
import PointsPerPassengerList from '../pointsPerPassengerList/PointsPerPassengerList';
import './PaymentData.css';

const propTypes = {
  id: PropTypes.string.isRequired,
  passengers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      lastName: PropTypes.string,
      passengerType: PropTypes.string
    })
  ).isRequired,
  departureFlight: PropTypes.shape({
    originAirport: PropTypes.string,
    finalDestination: PropTypes.string,
    category: PropTypes.string,
    prices: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        passenger: PropTypes.string,
        value: PropTypes.number
      })
    )
  }).isRequired,
  returnFlight: PropTypes.shape({
    originAirport: PropTypes.string,
    finalDestination: PropTypes.string,
    category: PropTypes.string,
    prices: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        passenger: PropTypes.string,
        value: PropTypes.number
      })
    )
  }),
  payment: PropTypes.shape({
    tax: PropTypes.number,
    binNumber: PropTypes.string,
    vendorCode: PropTypes.string
  }).isRequired,
  totalPoints: PropTypes.number.isRequired,
  totalValue: PropTypes.number.isRequired,
  installment: PropTypes.shape({
    totalAmount: PropTypes.number,
    installmentValue: PropTypes.number,
    numberOfInstallments: PropTypes.number,
    interestsInstallment: PropTypes.bool
  })
};

const defaultProps = {
  returnFlight: null
};

class PaymentData extends React.Component {
  constructor(props) {
    super(props);

    let returnFlightPrices;
    if (props.returnFlight) {
      returnFlightPrices = this.buildPassengers(
        props.passengers,
        props.returnFlight.category,
        props.returnFlight.prices
      );
    }

    this.state = {
      departureFlightPrices: this.buildPassengers(
        props.passengers,
        props.departureFlight.category,
        props.departureFlight.prices
      ),
      returnFlightPrices: returnFlightPrices
    };
  }

  getNameAndPoints(passenger, pricesPerCategory) {
    return {
      completeName: `${passenger.name} ${passenger.lastName}`,
      points: pricesPerCategory[passenger.passengerType]
    };
  }

  buildPassengers(passengersList, category, prices) {
    let pointsPerPassenger = [];
    let pricesPerCategory = prices
      .filter(price => price.category === category)
      .reduce((map, obj) => {
        map[obj.passenger] = obj.value;
        return map;
      }, {});

    let i;
    for (i = 0; i < passengersList.length; i++) {
      let passenger = passengersList[i];
      pointsPerPassenger.push(this.getNameAndPoints(passenger, pricesPerCategory));

      let infant = passenger.infant;
      if (infant) {
        pointsPerPassenger.push(this.getNameAndPoints(infant, pricesPerCategory));
      }
    }

    return pointsPerPassenger;
  }

  getPointsFormatted(value) {
    const pattern = /\B(?=(\d{3})+(?!\d))/g;
    return value.toString().replace(pattern, '.');
  }

  getValueFormatted(value) {
    return value
      .toFixed(2)
      .replace('.', ',')
      .replace(/(\d)(?=(\d{3})+,)/g, '$1.');
  }

  renderPricePerPassengerFlight(flight, isDeparture) {
    if (flight) {
      return (
        <PointsPerPassengerList
          isDepartureFlight={isDeparture}
          origin={flight.originAirport}
          destination={flight.finalDestination}
          passengers={
            isDeparture ? this.state.departureFlightPrices : this.state.returnFlightPrices
          }
        />
      );
    }
  }

  renderCardFlag(flag) {
    return <div className={`PaymentData__card ${flag.toLowerCase()}`} />;
  }

  renderSeparator() {
    if (this.state.returnFlightPrices) {
      return (
        <Col
          xs={12}
          smHidden={true}
          mdHidden={true}
          lgHidden={true}
          className="PaymentData__passengerList--separator">
          <div />
        </Col>
      );
    }
  }

  renderTotalPayment (value, installment) {

    if (installment == undefined || installment.numberOfInstallments == 1 || installment.numberOfInstallments == 0) {
      return (
        <Col xs={12} md={6} className="PaymentData__formOfPayment--value">
          {this.renderCardFlag(this.props.payment.vendorCode)}
          <span className="PaymentData__cardInfo">{`•••• ${this.props.payment.binNumber}`}</span>
          <span className="PaymentData__cash">
            {I18n.translate('components.paymentData.cashPaymentBRL', {
              cipher: (
                <span className="PaymentData__cipher">
                  {I18n.translate('components.paymentData.BRLCipher')}
                </span>
              ),
              value: (
                <span className="PaymentData__value">
                  {this.getValueFormatted(value)}
                </span>
              )
            })}
          </span>
        </Col>
      )
    }
    
    if (installment != undefined && installment.numberOfInstallments > 1) {
      return (
        <Col xs={12} md={6} className="PaymentData__formOfPayment--value">
          {this.renderCardFlag(this.props.payment.vendorCode)}
            <span className="PaymentData__cardInfo">{`•••• ${this.props.payment.binNumber}`}</span>
            <span className="PaymentData__cash">
              {I18n.translate('components.paymentData.valueInBrl', {
                cipher: (
                  <span className="PaymentData__cipher">
                    {I18n.translate('components.paymentData.BRLCipher')}
                  </span>
                ),
                value: (<span>{this.getValueFormatted(installment.totalAmount)}</span>)
              })}
          {' em ' + installment.numberOfInstallments + ' x '}
          <span className="text-500">
          {installment.interestsInstallment ? I18n.translate('features.paymentDetail.withInterests') :
           I18n.translate('features.paymentDetail.withoutInterests')}
          </span>
          </span>
        </Col>
      )
    }
  }

  render() {
    return (
      <div id={`PaymentData--${this.props.id}`} className="PaymentData">
        <Row className="PaymentData__passengerList">
          <Col xs={12} md={6}>
            {this.renderPricePerPassengerFlight(this.props.departureFlight, true)}
          </Col>
          {this.renderSeparator()}
          <Col xs={12} md={6}>
            {this.renderPricePerPassengerFlight(this.props.returnFlight, false)}
          </Col>
        </Row>
        <Row className="PaymentData__information PaymentData__totalInPoints">
          <Col xs={6} className="PaymentData__information--label">
            {I18n.translate('components.paymentData.totalPoints')}
          </Col>
          <Col xs={6} className="PaymentData__information--pointsValue">
            {I18n.translate('components.paymentData.valueInPts', {
              value: <span>{this.getPointsFormatted(this.props.totalPoints)}</span>,
              cipher: (
                <span className="PaymentData__information--pointsValue--pts">
                  {I18n.translate('components.paymentData.ptsCipher')}
                </span>
              )
            })}
          </Col>
        </Row>
        <Row className="PaymentData__information PaymentData__totalInTax">
          <Col xsHidden xs={6} className="PaymentData__information--textAlign">
            {I18n.translate('components.paymentData.totalTaxAndMoney')}
          </Col>
          <Col
            xsHidden
            xs={6}
            className="PaymentData__information--cipherValue PaymentData__information--taxValueDesktop">
            {I18n.translate('components.paymentData.valueInBrl', {
              value: <span>{this.getValueFormatted(this.props.totalValue)}</span>,
              cipher: (
                <span className="PaymentData__information--cipherValue--cipher">
                  {I18n.translate('components.paymentData.BRLCipher')}
                </span>
              )
            })}
          </Col>
          <Col smHidden mdHidden lgHidden xs={6} className="PaymentData__information">
            {I18n.translate('components.paymentData.totalTaxAndMoney')}
          </Col>
          <Col
            smHidden
            mdHidden
            lgHidden
            xs={6}
            className="PaymentData__information--cipherValue PaymentData__information--taxValueMobile">
            {I18n.translate('components.paymentData.valueInBrl', {
              value: <span>{this.getValueFormatted(this.props.totalValue)}</span>,
              cipher: (
                <span className="PaymentData__information--cipherValue--cipher">
                  {I18n.translate('components.paymentData.BRLCipher')}
                </span>
              )
            })}
          </Col>
          <Col xsHidden={true} md={12} className="PaymentData__separator">
            <div />
          </Col>
        </Row>
        <Row className="PaymentData__information PaymentData__line">
          <Col
            xs={12}
            md={6}
            className="PaymentData__information--label PaymentData__formOfPayment">
            {I18n.translate('components.paymentData.formOfPaymenyt')}
          </Col>
          {this.renderTotalPayment(this.props.totalValue, this.props.installment)}
        </Row>
      </div>
    );
  }
}

PaymentData.propTypes = propTypes;
PaymentData.defaultProps = defaultProps;

export default PaymentData;



// WEBPACK FOOTER //
// ./src/components/bookingResume/paymentData/PaymentData.jsx